import React, { FC } from 'react'
import classNames from 'classnames'
import { ColorsV2 } from '../../../design-system/colors_v2'
import { getV3ColorCssVariable } from '../../../design-system/colors_v3'
import { formatFr } from '../../../misc/date.utilities'
import { getVigilancePictogram } from '../../../misc/drug.utilities'
import { isDefined } from '../../../misc/functions.utilities'
import { DrugGenericTypes } from '../../../model/Drug'
import { InlineTag, TooltipWrapper, Icon } from '../../shared'
import { DrugThumbnailProps } from './DrugThumbnail.model'
import { computeActivePrinciplesLabel, computePriceInformations } from './DrugThumbnail.utils'
import styles from './DrugThumbnail.module.scss'

export const DrugThumbnail: FC<DrugThumbnailProps> = ({
  drug,
  size,
  treatment,
  prescription,
  displayInfo,
  isAdditionalDrug,
}) => {
  const priceInformations = computePriceInformations({
    drug,
    prescriptionPrice: prescription?.price,
  })
  const activesPrinciples = computeActivePrinciplesLabel(drug)
  const vigilancePictogram = getVigilancePictogram(drug.vigilanceLevel)

  return (
    <div className={styles.drugThumbnailContainer}>
      <span className={classNames(styles.drugThumbnailContent, { [styles.big]: size === 'big' })}>
        <span>
          {isAdditionalDrug && 'et '}
          {displayInfo ? drug.summary : drug.name}
          <span className={styles.drugThumbnailGeneric}>
            {drug.genericType === DrugGenericTypes.GENERIC && (
              <InlineTag color="lightGray">Générique</InlineTag>
            )}
            {drug.genericType === DrugGenericTypes.REFERENT && (
              <InlineTag color="lightGray">Référent</InlineTag>
            )}
            {drug.drugInSport && <InlineTag color="lightGray">Dopant</InlineTag>}
            {drug.isNarcotic && <InlineTag color="lightGray">Stupéfiant</InlineTag>}
            {isDefined(drug.bioSimilarGroup) && (
              <InlineTag color="lightGray">
                <span className={styles.bioSimilarGroup}>Groupe Bio-Similaire</span>
                <Icon
                  size="nano"
                  icon="infoCircle"
                  color={ColorsV2.backgroundDark3}
                  title={`${drug.bioSimilarGroup.name} (Groupe ${drug.bioSimilarGroup.id})\n${drug.bioSimilarGroup.comment}`}
                />
              </InlineTag>
            )}
            {drug.restrictedPrescriptions.length > 0 && (
              <InlineTag color="lightGray">
                <span className={styles.restreintPrescription}>Prescription Restreinte</span>
                <Icon
                  size="nano"
                  icon="infoCircle"
                  color={ColorsV2.backgroundDark3}
                  title={drug.restrictedPrescriptions.reduce(
                    (title, info) => `${title}\n${info}`,
                    '',
                  )}
                />
              </InlineTag>
            )}
            {isDefined(treatment?.drugConfig.drug.sideEffects) &&
              treatment?.drugConfig.drug.sideEffects.map(({ frequencyDisplayName, title }) => (
                <InlineTag color="lightGray">
                  {title}: {frequencyDisplayName ?? 'Fréquence inconnue'}
                </InlineTag>
              ))}
          </span>
        </span>
        {displayInfo && (
          <div className={styles.drugThumbnailTooltip}>
            <TooltipWrapper
              content={<div className={styles.tooltipDrugName}>{drug.name}</div>}
              pointerDirection="right"
            >
              <Icon size="micro" icon="infoCircle" color={ColorsV2.backgroundDark1} />
            </TooltipWrapper>
          </div>
        )}
      </span>
      <div className={styles.drugThumbnailInformationsContainer}>
        <div className={styles.drugThumbnailInformationsContent}>
          {activesPrinciples && (
            <span className={styles.drugThumbnailInformations}>{activesPrinciples}</span>
          )}
          {priceInformations && (
            <span className={styles.drugThumbnailInformations}>{priceInformations}</span>
          )}
          {treatment?.drugConfig.posologySummary && (
            <span className={styles.drugThumbnailInformations}>
              {treatment?.drugConfig.posologySummary}
            </span>
          )}
          <div className={styles.treatmentDates}>
            {treatment?.startDate && (
              <>
                <Icon
                  icon="calendar"
                  size="nano"
                  color={getV3ColorCssVariable('shades', 'shade4')}
                />
                <span>{formatFr(treatment.startDate, 'dd/MM/yyyy')}</span>
              </>
            )}
            {treatment?.endDate && (
              <>
                <Icon
                  icon="arrow"
                  rotate={90}
                  size="nano"
                  color={getV3ColorCssVariable('shades', 'shade4')}
                />
                <Icon
                  icon="calendar"
                  size="nano"
                  color={getV3ColorCssVariable('shades', 'shade4')}
                />
                <span>{formatFr(treatment.endDate, 'dd/MM/yyyy')}</span>
              </>
            )}
          </div>
        </div>
        {vigilancePictogram && size === 'big' && (
          <img
            className={styles.vigilancePictogram}
            src={vigilancePictogram.pictogram}
            alt={vigilancePictogram.tooltip}
            title={vigilancePictogram.tooltip}
          />
        )}
      </div>
    </div>
  )
}
